import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { AuthenticationLayoutComponent } from './layouts/authentication-layout/authentication-layout.component';
import { ProtectedGuard } from './shared/guards/protected.guard';
import { AuthGuard } from './shared/guards/auth.guard';
import { FallbackRedirectGuard } from './shared/guards/fallback-redirect.guard';
import { CompanyApprovedGuard } from './shared/guards/company-approved.guard';
import { AdminRoleGuard } from './shared/guards/admin-role.guard';
import { SuperAdminGuard } from './shared/guards/super-admin.guard';
import { AunakProAppLandingGuard } from './shared/guards/aunak-pro-app-landing.guard';
import { RedirectAunakProAppUsersGuard } from './shared/guards/redirect-aunak-pro-app-users.guard';

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [FallbackRedirectGuard],
    component: AuthenticationLayoutComponent,
  },
  {
    path: 'login',
    component: AuthenticationLayoutComponent,
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./auth/login/login.module').then((m) => m.LoginModule),
  },
  {
    path: 'login-phone',
    component: AuthenticationLayoutComponent,
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./auth/login-phone/login-phone.module').then(
        (m) => m.LoginPhoneModule,
      ),
  },
  {
    path: 'register',
    component: AuthenticationLayoutComponent,
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./auth/register/register.module').then((m) => m.RegisterModule),
  },
  {
    path: 'auth/accept-invite',
    component: AuthenticationLayoutComponent,
    loadChildren: () =>
      import('./auth/accept-invite/accept-invite.module').then(
        (m) => m.AcceptInviteModule,
      ),
  },
  {
    path: 'auth/forgot-password',
    component: AuthenticationLayoutComponent,
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./auth/forgot-password/forgot-password.module').then(
        (m) => m.ForgotPasswordModule,
      ),
  },
  {
    path: 'auth/reset-password',
    component: AuthenticationLayoutComponent,
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./auth/reset-password/reset-password.module').then(
        (m) => m.ResetPasswordModule,
      ),
  },
  {
    path: 'signin',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./mobile-auth/login/login.module').then((m) => m.LoginModule),
  },
  {
    path: 'signup',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./mobile-auth/signup/signup.module').then((m) => m.SignupModule),
  },
  {
    path: 'aunak-pro-app',
    component: AuthenticationLayoutComponent,
    canActivate: [ProtectedGuard, AunakProAppLandingGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./pages/aunak-pro-app-landing/aunak-pro-app-landing.module').then(
            (m) => m.AunakProAppLandingModule,
          ),
      },
    ],
  },
  {
    path: 'sadad',
    loadChildren: () =>
      import('./sadad/payment/payment.module').then((m) => m.PaymentModule),
  },
  {
    path: 'payment-fail',
    loadChildren: () =>
      import('./sadad/payment-failure/payment-failure.module').then(
        (m) => m.PaymentFailureModule,
      ),
  },
  {
    path: 'payment-success',
    loadChildren: () =>
      import('./sadad/payment-success/payment-success.module').then(
        (m) => m.PaymentSuccessModule,
      ),
  },

  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [ProtectedGuard],
    children: [
      {
        path: 'admin/payment-requests',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/admin/payment-requests/payment-requests.module').then(
            (m) => m.PaymentRequestsModule,
          ),
      },
      {
        path: 'withdrawal-requests',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/admin/withdrawal-requests/withdrawal-requests.module').then(
            (m) => m.WithdrawalRequestsModule,
          ),
      },
      {
        path: 'dashboard',
        canActivate: [RedirectAunakProAppUsersGuard, AdminRoleGuard],
        loadChildren: () =>
          import('./pages/dashboard/dashboard.module').then(
            (m) => m.DashboardModule,
          ),
      },
      {
        path: 'users',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/users/users.module').then((m) => m.UsersModule),
      },
      {
        path: 'deletion-requests',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/admin/deletion-requests/deletion-requests.module').then(
            (m) => m.DeletionRequestsModule,
          ),
      },
      {
        // TEMPORARY QA panel — remove with pages/admin/qa-test-panel
        path: 'qa-test-panel',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/admin/qa-test-panel/qa-test-panel.module').then(
            (m) => m.QaTestPanelModule,
          ),
      },
      {
        path: 'analytics',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/analytics/analytics.module').then(
            (m) => m.AnalyticsModule,
          ),
      },
      {
        path: 'admin/profile',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/profile/profile.module').then((m) => m.ProfileModule),
      },
      {
        path: 'admin/orders',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/orders/orders.module').then((m) => m.OrdersModule),
      },
      {
        path: 'feedbacks',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/feedbacks/feedbacks.module').then(
            (m) => m.FeedbacksModule,
          ),
      },
      {
        path: 'offers-ads',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/offers-ads/offers-ads.module').then(
            (m) => m.OffersAdsModule,
          ),
      },
      {
        path: 'admin-offers',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/admin-offers/admin-offers.module').then(
            (m) => m.AdminOffersModule,
          ),
      },
      {
        path: 'app-control',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/app-control/app-control.module').then(
            (m) => m.AppControlModule,
          ),
      },
      {
        path: 'ads',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/admin-ads/admin-ads.module').then(
            (m) => m.AdminAdsModule,
          ),
      },
      {
        path: 'admin/payments',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/payments/payments.module').then(
            (m) => m.PaymentsModule,
          ),
      },
      {
        path: 'nojoom',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/noujoum-ooredoo/noujoum-ooredoo.module').then(
            (m) => m.NoujoumOoredooModule,
          ),
      },
      {
        path: 'bakat',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/bakat/bakat.module').then((m) => m.BakatModule),
      },
      {
        path: 'employees',
        canActivate: [SuperAdminGuard],
        loadChildren: () =>
          import('./pages/employees/employees.module').then(
            (m) => m.EmployeesModule,
          ),
      },
      {
        path: 'dynamic-old-services',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/dynamic-services/dynamic-services.module').then(
            (m) => m.DynamicServicesModule,
          ),
      },
      {
        path: 'app-layouts',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/app-layouts/app-layouts.module').then(
            (m) => m.AppLayoutsModule,
          ),
      },
      {
        path: 'dynamic-services',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/admin-services/admin-services.module').then(
            (m) => m.AdminServicesModule,
          ),
      },
      {
        path: 'approvals',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/approvals-users/approvals-users.module').then(
            (m) => m.ApprovalsUsersModule,
          ),
      },
      {
        path: 'admin/help-center',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/support/support.module').then((m) => m.SupportModule),
      },
      {
        path: 'legalss',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/legals/legals.module').then((m) => m.LegalsModule),
      },
      {
        path: 'legals',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/new-legals/new-legals.module').then(
            (m) => m.NewLegalsModule,
          ),
      },
      {
        path: 'my-providers',
        canActivate: [CompanyApprovedGuard],
        loadChildren: () =>
          import('./company/providers/providers.module').then(
            (m) => m.ProvidersModule,
          ),
      },
      {
        path: 'my-dashboard',
        canActivate: [CompanyApprovedGuard],
        loadChildren: () =>
          import('./company/my-dashboard/my-dashboard.module').then(
            (m) => m.MyDashboardModule,
          ),
      },
      {
        path: 'profile',
        loadChildren: () =>
          import('./company/my-profile/my-profile.module').then(
            (m) => m.MyProfileModule,
          ),
      },
      {
        path: 'account-pending',
        loadChildren: () =>
          import('./company/account-pending/account-pending.module').then(
            (m) => m.AccountPendingModule,
          ),
      },
      {
        path: 'help-center',
        canActivate: [CompanyApprovedGuard],
        loadChildren: () =>
          import('./company/my-help-center/my-help-center.module').then(
            (m) => m.MyHelpCenterModule,
          ),
      },
      {
        path: 'settings',
        canActivate: [CompanyApprovedGuard],
        loadChildren: () =>
          import('./company/my-settings/my-settings.module').then(
            (m) => m.MySettingsModule,
          ),
      },
      {
        path: 'faqs',
        canActivate: [CompanyApprovedGuard],
        loadChildren: () =>
          import('./company/my-faqs/my-faqs.module').then(
            (m) => m.MyFaqsModule,
          ),
      },
      {
        path: 'guides',
        canActivate: [CompanyApprovedGuard],
        loadChildren: () =>
          import('./company/my-guides/my-guides.module').then(
            (m) => m.MyGuidesModule,
          ),
      },
      {
        path: 'support',
        loadChildren: () =>
          import('./company/my-contacts/my-contacts.module').then(
            (m) => m.MyContactsModule,
          ),
      },
      {
        path: 'orders',
        canActivate: [CompanyApprovedGuard],
        loadChildren: () =>
          import('./company/my-orders/my-orders.module').then(
            (m) => m.MyOrdersModule,
          ),
      },
      {
        path: 'earnings',
        canActivate: [CompanyApprovedGuard],
        loadChildren: () =>
          import('./company/my-earnings/my-earnings.module').then(
            (m) => m.MyEarningsModule,
          ),
      },
      {
        path: 'reports',
        loadChildren: () =>
          import('./company/my-reports/my-reports.module').then(
            (m) => m.MyReportsModule,
          ),
      },
      {
        path: 'business-hours',
        canActivate: [CompanyApprovedGuard],
        loadChildren: () =>
          import('./company/business-hours/business-hours.module').then(
            (m) => m.BusinessHoursModule,
          ),
      },
      {
        path: 'my-ads',
        canActivate: [CompanyApprovedGuard],
        loadChildren: () =>
          import('./company/my-ads/my-ads.module').then((m) => m.MyAdsModule),
      },
      {
        path: 'admin/docs',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/admin-docs/admin-docs.module').then(
            (m) => m.AdminDocsModule,
          ),
      },
      {
        path: 'cars',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/car-brand-models/car-brand-models.module').then(
            (m) => m.CarBrandModelsModule,
          ),
      },
      {
        path: 'shop-management',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/shop-management/shop-management.module').then(
            (m) => m.ShopManagementModule,
          ),
      },
      {
        path: 'greetings',
        canActivate: [AdminRoleGuard],
        loadChildren: () =>
          import('./pages/greetings/greetings.module').then(
            (m) => m.GreetingsModule,
          ),
      },
      {
        path: 'my-shop',
        loadChildren: () =>
          import('./company/my-shop/my-shop.module').then(
            (m) => m.MyShopModule,
          ),
      },
      {
        path: 'notifications',
        canActivate: [CompanyApprovedGuard],
        loadChildren: () =>
          import('./company/notifications/notifications.module').then(
            (m) => m.NotificationsModule,
          ),
      },
      {
        path: 'cancellations',
        canActivate: [CompanyApprovedGuard],
        loadChildren: () =>
          import('./company/cancellations/cancellations.module').then(
            (m) => m.CancellationsModule,
          ),
      },
      {
        path: 'docs',
        canActivate: [CompanyApprovedGuard],
        loadChildren: () =>
          import('./company/docs/docs.module').then((m) => m.DocsModule),
      },
      {
        path: 'my-services',
        canActivate: [CompanyApprovedGuard],
        loadChildren: () =>
          import('./company/my-services/my-services.module').then(
            (m) => m.MyServicesModule,
          ),
      },
      {
        path: '**',
        canActivate: [FallbackRedirectGuard],
        component: AuthenticationLayoutComponent,
      },
    ],
  },

  {
    path: '**',
    canActivate: [FallbackRedirectGuard],
    component: AuthenticationLayoutComponent,
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
